import { eventsFlux, EventTypes, nodesFlux, useEditorSelector } from '@candulabs/craft';
import { nanoid } from 'nanoid';
import React, { ReactElement, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { colors } from 'styleguide';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import Node from 'craft/components/Node';
import { createVariantsSelector } from 'craft/components/SelectedToolbar/VariantsSelector';
import { NO_ACTION } from 'craft/constants';
import { useEditor, useEditorNode, useNodeContext } from 'craft/hooks';
import { useNodeExists } from 'craft/hooks/useNodeExists';
import { CraftNode, Resolver, ToolboxComponentSections } from 'craft/types';

import { CheckListItem } from './CheckListItem';
import { EditableTitle } from './EditableTitle';
import { NewItemInput } from './NewItemInput';
import { CheckListGroupSettings, ChecklistGroupVariantSelector } from './toolbox';

const Title = styled(EditableTitle)`
  font-size: 24px;
  line-height: 160%;
  color: ${colors('tertiary', 2)};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  padding-right: 16px;
  padding-left: 16px;
  margin-bottom: 48px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

interface Props {
  children?: ReactElement | ReactElement[];
  title: string;
  variant?: string;
}

export const CheckListGroup: Resolver<Props> = ({ children, title, variant }: Props) => {
  const { id } = useNodeContext();
  const { setProps, setCustomWithoutHistory } = useEditorNode(id);
  const customSelector = useMemo(() => nodesFlux.selectors.getNodeCustom(id), [id]);
  const custom = useEditorSelector(customSelector);
  const nodeExists = useNodeExists(id);
  const { query, actions } = useEditor();

  const createItem = (itemTitle: string) => {
    const stateId = nanoid(6);
    const node = query
      .parseFreshNode({
        data: {
          type: CheckListItem,
          props: { title: itemTitle },
          custom: {
            states: { [stateId]: {} },
          },
        },
      })
      .toNode();
    actions.addNodes(node, id);
  };

  const onEdit = (value: string) => setProps({ title: value });

  useEffect(() => {
    if (!nodeExists) {
      // For some reason we have a render while we're being deleted
      // Custom defaults to an empty object so we can't rely on that, see:
      // https://github.com/candulabs/craft/blob/db96a801ac836533012e10cd53f8e9c1620ae18a/src/core/nodes/nodesFlux.ts#L100
      return;
    }

    // Make sure group always has unique id for analytics
    if (!custom.custom?.groupId) {
      setCustomWithoutHistory({
        ...custom,
        custom: {
          ...custom.custom,
          groupId: nanoid(6),
        },
      });
    }
  }, [custom?.custom?.groupId]);

  const checkListClassName = toCssSelector('checklist', {
    output: 'html',
    variant: variant ? { variant } : undefined,
  });

  const titleClassName = toCssSelector('checklist', {
    className: 'groupTitle',
    output: 'html',
  });

  const selectedSelector = useMemo(
    () => eventsFlux.selectors.doesNodeHaveEvent(id, EventTypes.selected),
    [id],
  );
  const isSelected = useEditorSelector(selectedSelector);

  return (
    <Node is={Container} className={checkListClassName}>
      <Header>
        <Title
          nodeId={id}
          data-focus-listener={NO_ACTION}
          className={titleClassName}
          tagName="span"
          key={id}
          html={title || ''}
          onEdit={onEdit}
          disabled={!isSelected}
        />
      </Header>
      {children}
      <NewItemInput onCreate={createItem} />
    </Node>
  );
};

CheckListGroup.craft = {
  displayName: 'Checklist',
  name: 'CheckListGroup',
  isCanvas: true,
  rules: {
    canMoveIn: (incomingNodes: CraftNode[]) =>
      incomingNodes.every((node) => node.data.name === 'CheckListItem'),
  },
};

CheckListGroup.toolbox = {
  component: [CheckListGroupSettings],
  schema: {
    [ToolboxComponentSections.NameAndType]: {
      variantSelector: ChecklistGroupVariantSelector,
    },
    [ToolboxComponentSections.Layout]: {
      blockedDisplays: ['block', 'inline-block'],
    },
    [ToolboxComponentSections.Border]: {
      isVisible: false,
    },
    [ToolboxComponentSections.Shadow]: {
      isVisible: false,
    },
  },
};

CheckListGroup.selectedToolbar = {
  customItems: [
    createVariantsSelector({
      componentName: 'checklist',
      getConditions: (props) =>
        (props.variant?.value
          ? { name: props.variant?.value, variant: props.variant?.value }
          : { name: 'base' }) as Record<string, string>,
      variantToProps: (variant) => ({
        variant: variant.conditions.variant,
      }),
    }),
  ],
};
